const FALLBACK = 'Unbenannter Mitarbeiter'

const ORG_PREFIXES = ['pm', 'mit', 'admin']

const GENERIC_MAILBOXES = new Set([
  'info',
  'kontakt',
  'mail',
  'office',
  'support',
  'noreply',
  'no-reply',
  'admin',
])

/**
 * Turns an email's local part into a readable name.
 *
 * Only used as a *fallback* when an employee has no `firstName`/`lastName` in
 * Firestore. Generic mailboxes (info@, kontakt@, …) collapse to the fallback
 * label so we never advertise a shared inbox as a person.
 */
export function beautifyEmailName(email: string | null | undefined): string {
  if (!email || !email.includes('@')) return FALLBACK

  const localPart = email.split('@')[0].trim().toLowerCase()
  if (!localPart) return FALLBACK

  if (GENERIC_MAILBOXES.has(localPart)) return FALLBACK

  let rest = localPart
  for (const prefix of ORG_PREFIXES) {
    if (rest.startsWith(`${prefix}.`)) {
      rest = rest.slice(prefix.length + 1)
      break
    }
  }

  const parts = rest.split(/[._-]+/).filter(Boolean)
  if (parts.length === 0) return FALLBACK

  return parts
    .map((p) =>
      p.length === 1
        ? `${p.toUpperCase()}.`
        : p.charAt(0).toUpperCase() + p.slice(1),
    )
    .join(' ')
}

export interface AssigneeNameInput {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

/**
 * Picks the best display name for a ticket assignee.
 *
 * 1. `firstName lastName` if either is set
 * 2. `beautifyEmailName(email)` otherwise
 * 3. Generic fallback if everything is empty
 */
export function resolveAssigneeDisplayName(input: AssigneeNameInput): string {
  const first = input.firstName?.trim() ?? ''
  const last = input.lastName?.trim() ?? ''
  const fromNames = [first, last].filter(Boolean).join(' ')
  if (fromNames) return fromNames

  return beautifyEmailName(input.email)
}
